// https://serverless-stack.com/chapters/handle-forgot-and-reset-password.html
// https://github.com/AnomalyInnovations/serverless-stack-demo-user-mgmt-client
import React, { useState, useEffect } from "react";
import { Auth } from "aws-amplify";
import { Link } from "react-router-dom";
import {
  Button,
  createTheme,
  TextField,
  Box,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useFormFields } from "../config/hooksLib";
import { onError } from "../config/errorLib";
import "../style/login.css";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Header from "./Header";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

export default function ResetPassword() {
  const [fields, handleFieldChange] = useFormFields({
    code: "",
    email: "",
    password: "", // pragma: allowlist secret
    confirmPassword: "", // pragma: allowlist secret
  });
  const [awaitingEmail, isEmailGiven] = useState(true);
  const [codeSent, setCodeSent] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  //helper text
  const [emailErr, setEmailErr] = useState("");
  const [passwordErr, setPasswordErr] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmationCodeErr, setConfirmationCodeErr] = useState(false);
  const [errMessage, setErrMessage] = useState("");

  useEffect(() => {
    if (fields.password !== "") {
      setPasswordErr(false);
    }
    if (fields.code !== "") {
      setConfirmationCodeErr(false);
    }
  }, [fields.password, fields.code]);

  function validateCodeForm() {
    return fields.email.length > 0;
  }

  function validateCodeExists() {
    return fields.code.length > 0;
  }

  function validatePassExists() {
    return fields.password.length > 0;
  }

  function validatePassMatch() {
    return (
      fields.password === fields.confirmPassword // pragma: allowlist secret
    );
  }

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  async function handleSendCodeClick(e) {
    if ((e && e.key === "Enter") || !e) {
      setIsSendingCode(true);
      setEmailErr("");
      try {
        await Auth.forgotPassword(fields.email);
        setCodeSent(true);
        isEmailGiven(false);
      } catch (error) {
        let message = onError(error);
        setIsSendingCode(false);
        if (message.includes("Username")) {
          setEmailErr(message);
        }
      }
    }
  }

  async function handleConfirmClick(event) {
    event.preventDefault();
    setPasswordErr(false);
    setErrMessage("");
    setConfirmationCodeErr(false);
    setIsConfirming(true);
    //check pass match
    if (!validatePassMatch()) {
      setPasswordErr(true);
      setIsConfirming(false);
      setErrMessage("Passwords do not match.");
      return;
    }
    if (!validateCodeExists()) {
      setConfirmationCodeErr(true);
      setIsConfirming(false);
      return;
    }
    if (!validatePassExists()) {
      setPasswordErr(true);
      setIsConfirming(false);
      setErrMessage("Password fields cannot be empty.");
      return;
    }
    try {
      await Auth.forgotPasswordSubmit(
        fields.email,
        fields.code,
        fields.password
      );
      setConfirmed(true);
    } catch (error) {
      console.log("error: ", error);
      if (error.code && error.code.includes("Code")) {
        onError(error);
        setConfirmationCodeErr(true);
        setIsConfirming(false);
      } else {
        setErrMessage(error.message);
        setPasswordErr(true);
        onError(error);
        setIsConfirming(false);
      }
    }
  }

  function handleClose() {
    window.location.href = "/";
  }

  function renderTitleHeader(page) {
    return (
      <div>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          style={{ width: "76vw", marginBottom: "2%", marginTop: "3%" }}
        >
          <span className="forgotPassTitle">Forgot Password</span>
          <Link to="/" style={{ all: "unset" }}>
            <span className="blueTouchableText">EXIT</span>
          </Link>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          style={{ width: "76vw" }}
        >
          <div className="filledLine" />
          <div className={page === 1 ? "unfilledLine" : "filledLine"} />
        </Box>
        <Box>
          <span className="stepText">
            {page === 1
              ? "Step 1 / 2 : User Information"
              : "Step 2 / 2: Create New Password"}
          </span>
        </Box>
      </div>
    );
  }

  function renderRequestCodeForm() {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {renderTitleHeader(1)}
        <Box
          style={{
            marginTop: "4vh",
            backgroundColor: "#ffffff",
            borderRadius: 4,
            padding: "30px 70px 50px 70px",
            width: "53vw",
            boxShadow: "0 2px 10px 0 rgba(0, 0, 0, 0.1)",
          }}
        >
          <span
            style={{
              color: "#404040",
              fontWeight: "bold",
              fontSize: 17,
            }}
          >
            User Information
          </span>
          <div style={{ marginTop: "34px" }} />
          <TextField
            variant="outlined"
            autoFocus
            margin="dense"
            id="email"
            label="Email Address"
            type="email"
            fullWidth
            InputLabelProps={{
              style: {
                fontSize: 13.5,
                color: "#666666",
              },
            }}
            inputProps={{
              style: {
                fontSize: 15,
                color: "#666666",
              },
            }}
            value={fields.email}
            onChange={handleFieldChange}
            onKeyPress={(e) => handleSendCodeClick(e)}
            helperText={emailErr}
            FormHelperTextProps={{
              style: {
                color: "red",
                fontSize: 12,
                display: emailErr !== "" ? "flex" : "none",
              },
            }}
          />
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          style={{ marginTop: 30 }}
        >
          <Link to="/" style={{ all: "unset" }}>
            <Button
              className="button-text"
              style={{
                color: "#666666",
                fontSize: 15,
                fontWeight: "bold",
                border: "1px solid #979797",
                height: 43,
                width: 142,
                borderRadius: 4,
                backgroundColor: "transparent",
              }}
            >
              BACK
            </Button>
          </Link>
          <Button
            onClick={() => handleSendCodeClick()}
            isLoading={isSendingCode}
            className="button-text"
            style={{
              color: "#ffffff",
              fontSize: 15,
              fontWeight: "bold",
              height: 43,
              width: 142,
              borderRadius: 4,
              backgroundColor: "#0073a8",
              marginLeft: 24,
            }}
          >
            SEND
          </Button>
        </Box>
      </div>
    );
  }

  function renderConfirmationForm() {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {renderTitleHeader(2)}
        <Box
          style={{
            marginTop: "4vh",
            backgroundColor: "#ffffff",
            borderRadius: 4,
            padding: "30px 70px 50px 70px",
            width: "53vw",
            boxShadow: "0 2px 10px 0 rgba(0, 0, 0, 0.1)",
          }}
        >
          <span
            style={{
              color: "#404040",
              fontWeight: "bold",
              fontSize: 17,
            }}
          >
            Create New Password
          </span>
          <div style={{ marginTop: "23px" }} />
          <TextField
            autoFocus
            margin="dense"
            id="code"
            label="Confirmation Code"
            type="tel"
            fullWidth
            InputLabelProps={{
              style: {
                fontSize: 13.5,
                color: "#666666",
              },
            }}
            inputProps={{
              style: {
                fontSize: 15,
                color: "#666666",
              },
            }}
            value={fields.code}
            onChange={handleFieldChange}
            helperText={
              confirmationCodeErr ? "Please verify confirmation code." : " "
            }
            FormHelperTextProps={{
              style: {
                color: "red",
                fontSize: 12,
              },
            }}
          />
          {/* <div style={{ marginTop: 10 }} /> */}
          <TextField
            margin="dense"
            id="password"
            label="New Password"
            type={showPassword ? "email" : "password"}
            fullWidth
            InputLabelProps={{
              style: {
                fontSize: 13.5,
                color: "#666666",
              },
            }}
            inputProps={{
              style: {
                fontSize: 15,
                color: "#666666",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? (
                      <VisibilityOff className="eyeIcon" fontSize="small" />
                    ) : (
                      <Visibility className="eyeIcon" fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            value={fields.password}
            onChange={handleFieldChange}
            helperText="Minimum 8 characters and must contain a number, lowercase, uppercase, and special character"
          />
          <TextField
            margin="dense"
            id="confirmPassword"
            label="Confirm New Password"
            type={showPassword ? "email" : "password"}
            fullWidth
            InputLabelProps={{
              style: {
                fontSize: 13.5,
                color: "#666666",
              },
            }}
            inputProps={{
              style: {
                fontSize: 15,
                color: "#666666",
              },
            }}
            value={fields.confirmPassword}
            onChange={handleFieldChange}
            helperText={
              passwordErr
                ? "Password fields cannot be empty."
                : fields.password !== fields.confirmPassword &&
                  fields.confirmPassword !== ""
                ? "Passwords do not match"
                : errMessage
            }
            FormHelperTextProps={{
              style: {
                color: "red",
                fontSize: 12,
                display:
                  (fields.password !== fields.confirmPassword &&
                    fields.confirmPassword !== "") ||
                  passwordErr
                    ? "flex"
                    : "none",
              },
            }}
          />
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          style={{ marginTop: 30 }}
        >
          <Link to="/" style={{ all: "unset" }}>
            <Button
              className="button-text"
              style={{
                color: "#666666",
                fontSize: 15,
                fontWeight: "bold",
                border: "1px solid #979797",
                height: 43,
                width: 142,
                borderRadius: 4,
                backgroundColor: "transparent",
              }}
            >
              BACK
            </Button>
          </Link>
          <Button
            onClick={handleConfirmClick}
            isLoading={isSendingCode}
            className="button-text"
            style={{
              color: "#ffffff",
              fontSize: 15,
              fontWeight: "bold",
              height: 43,
              width: 142,
              borderRadius: 4,
              backgroundColor: "#0073a8",
              marginLeft: 24,
            }}
          >
            CONFIRM
          </Button>
        </Box>
      </div>
    );
  }

  function renderSuccessMessage() {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          style={{
            marginTop: 96,
            backgroundColor: "#ffffff",
            borderRadius: 4,
            padding: "36px 70px 32px 70px",
            width: "53vw",
            textAlign: "center",
            boxShadow: "0 2px 10px 0 rgba(0, 0, 0, 0.1)",
          }}
        >
          <div
            style={{
              flexDirection: "row",
              display: "flex",
              alignItems: "center",
            }}
          >
            <CheckCircleIcon style={{ color: "#67bd45", fontSize: "22px" }} />
            <span
              style={{
                color: "#404040",
                fontWeight: "bold",
                fontSize: 17,
                marginLeft: 9,
              }}
            >
              Password Reset Successful!
            </span>
          </div>
          <span className="successText">
            Your password has been changed successfully.
          </span>
        </Box>
        <div style={{ marginTop: 30 }} />
        <Link to="/" style={{ all: "unset", fontSize: "20" }}>
          <Button
            className="button-text"
            style={{
              color: "#ffffff",
              fontSize: 15,
              fontWeight: "bold",
              borderColor: "white",
              height: 43,
              width: 142,
              borderRadius: 4,
              backgroundColor: "#0073a8",
            }}
          >
            LOG IN
          </Button>
        </Link>
      </div>
    );
  }
  return (
    <div className="ResetPassword">
      <Header auth={false} />
      <div>
        {!codeSent
          ? renderRequestCodeForm()
          : !confirmed
          ? renderConfirmationForm()
          : renderSuccessMessage()}
      </div>
    </div>
  );
}
